import {
  Activity,
  ArrowLeft,
  BarChart3,
  Bell,
  Briefcase,
  Calendar,
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ChevronUp,
  CircleHelp,
  Copy,
  Eye,
  EyeOff,
  FileText,
  Grid3x3,
  Info,
  Languages,
  LayoutDashboard,
  List,
  type LucideIcon,
  Menu,
  MonitorCheck,
  MoreHorizontal,
  MoreVertical,
  Pencil,
  Plane,
  Plus,
  PlusCircle,
  Receipt,
  Search,
  Settings,
  Star,
  Table2,
  Tablet,
  Trash2,
  TriangleAlert,
  Users,
  X,
} from "lucide-react";
import type { SVGProps } from "react";

type GlyphProps = SVGProps<SVGSVGElement> & { size: number; strokeWidth: number };

/*
 * Clockify-specific glyphs are hand-drawn rather than copied: the spec forbids shipping
 * Clockify's own SVG assets (branding/IP), so these only echo their shapes.
 */
function svg(size: number, props: SVGProps<SVGSVGElement>, children: React.ReactNode) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false" {...props}>
      {children}
    </svg>
  );
}

const custom = {
  tracker: ({ size, strokeWidth, ...p }: GlyphProps) =>
    svg(
      size,
      p,
      <g stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round">
        <circle cx="10" cy="11" r="6.5" />
        <path d="M10 11V7.5M8 2.5h4M15 5.5l1.2-1.2" />
      </g>,
    ),
  play: ({ size, ...p }: GlyphProps) => svg(size, p, <path d="M6 4.2v11.6L15.5 10 6 4.2Z" fill="currentColor" />),
  stop: ({ size, ...p }: GlyphProps) =>
    svg(size, p, <rect x="5" y="5" width="10" height="10" rx="1" fill="currentColor" />),
  billable: ({ size, strokeWidth, ...p }: GlyphProps) =>
    svg(
      size,
      p,
      <path
        d="M13.2 6.3c-.5-1-1.7-1.6-3.2-1.6-1.9 0-3.2.9-3.2 2.3 0 3.2 6.6 1.8 6.6 5 0 1.5-1.4 2.4-3.4 2.4-1.6 0-2.9-.7-3.4-1.8M10 3v14"
        stroke="currentColor"
        strokeWidth={strokeWidth + 0.5}
        strokeLinecap="round"
      />,
    ),
  tag: ({ size, strokeWidth, ...p }: GlyphProps) =>
    svg(
      size,
      p,
      <g stroke="currentColor" strokeWidth={strokeWidth} strokeLinejoin="round">
        <path d="M3 3.5h6.3l7.4 7.4a1 1 0 0 1 0 1.4l-5 5a1 1 0 0 1-1.4 0L3 9.8V3.5Z" />
        <circle cx="6.5" cy="7" r="1.2" />
      </g>,
    ),
  project: ({ size, strokeWidth, ...p }: GlyphProps) =>
    svg(
      size,
      p,
      <path
        d="M2.5 5.5a1 1 0 0 1 1-1h4l1.6 1.8h7.4a1 1 0 0 1 1 1v8.2a1 1 0 0 1-1 1h-13a1 1 0 0 1-1-1v-10Z"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
      />,
    ),
  clients: ({ size, strokeWidth, ...p }: GlyphProps) =>
    svg(
      size,
      p,
      <g stroke="currentColor" strokeWidth={strokeWidth} strokeLinejoin="round">
        <rect x="2.5" y="6" width="15" height="10.5" rx="1" />
        <path d="M7 6V4.5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1V6M2.5 10.5h15" />
      </g>,
    ),
} satisfies Record<string, (p: GlyphProps) => React.ReactNode>;

const lucide = {
  timesheet: Table2,
  calendar: Calendar,
  "calendar-days": CalendarDays,
  dashboard: LayoutDashboard,
  reports: BarChart3,
  projects: FileText,
  team: Users,
  tags: Star,
  kiosk: Tablet,
  schedule: CalendarDays,
  expenses: Receipt,
  "time-off": Plane,
  activity: Activity,
  approvals: CheckCircle2,
  invoices: FileText,
  monitoring: MonitorCheck,
  settings: Settings,
  briefcase: Briefcase,
  bell: Bell,
  help: CircleHelp,
  grid: Grid3x3,
  menu: Menu,
  search: Search,
  plus: Plus,
  "plus-circle": PlusCircle,
  check: Check,
  close: X,
  "chevron-down": ChevronDown,
  "chevron-up": ChevronUp,
  "chevron-left": ChevronLeft,
  "chevron-right": ChevronRight,
  "chevrons-left": ChevronsLeft,
  "chevrons-right": ChevronsRight,
  "arrow-left": ArrowLeft,
  "more-horizontal": MoreHorizontal,
  "more-vertical": MoreVertical,
  list: List,
  info: Info,
  languages: Languages,
  warning: TriangleAlert,
  edit: Pencil,
  trash: Trash2,
  copy: Copy,
  eye: Eye,
  "eye-off": EyeOff,
  star: Star,
} satisfies Record<string, LucideIcon>;

export type IconName = keyof typeof custom | keyof typeof lucide;

export const iconNames = [...Object.keys(custom), ...Object.keys(lucide)] as IconName[];

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, "name"> {
  name: IconName;
  /** Clockify's nav and ui icons are drawn on a 20px grid. */
  size?: number;
  strokeWidth?: number;
  /** Gives the icon an accessible name; without it the icon is decorative. */
  label?: string;
}

export function Icon({ name, size = 20, strokeWidth = 1.25, label, ...rest }: IconProps) {
  const a11y = label ? { role: "img", "aria-label": label, "aria-hidden": undefined } : { "aria-hidden": true };
  if (name in custom) {
    const Glyph = custom[name as keyof typeof custom];
    return <Glyph size={size} strokeWidth={strokeWidth} {...rest} {...(a11y as object)} />;
  }
  const Glyph = lucide[name as keyof typeof lucide];
  return <Glyph size={size} strokeWidth={strokeWidth} {...rest} {...(a11y as object)} />;
}
